import { FC } from 'react';
import { Divider } from 'antd';
import { TbCashBanknote, TbChartLine, TbFileText } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { DropdownMenuLabel } from '@/shared/components/dropdowns/DropdownPanel';
import { FinancialValue } from '@/shared/components/financial-values';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { ChartTooltipContent, PointerTrackingTooltip } from '@/shared/components/tooltips';
import { dayjs } from '@/utilities';

import { useFinancialOverviewViewPreference } from '../../hooks/useFinancialOverviewViewPreference';
import { FinancialOverviewCorporateDebtData as CorporateDebtData } from '../../types';
import { FinancialMetricFormulaTooltip } from '../FinancialMetricFormula';
import {
    createFinancialFormula,
    financialFormulaOperand,
    financialFormulaOperator,
} from '../FinancialMetricFormula/formula';
import { FinancialOverviewMetricCardGrid } from '../FinancialOverviewMetricCardGrid';
import { FinancialOverviewPanel } from '../FinancialOverviewPanel';
import DebtInstrumentsList from './DebtInstrumentsList';
import DebtTrendView from './DebtTrendView';

interface FinancialOverviewCorporateDebtProps {
    preferenceKey?: string;
    section?: CorporateDebtData;
    tickerSymbol?: string;
}

type CorporateDebtView = 'instruments' | 'trend';

const isCorporateDebtView = (value: unknown): value is CorporateDebtView =>
    value === 'instruments' || value === 'trend';

const totalDebtFormula = createFinancialFormula(
    financialFormulaOperand('longTermDebt', 'Long-Term Debt', 'balance-sheet'),
    financialFormulaOperator('+'),
    financialFormulaOperand('shortTermDebt', 'Short-Term Debt', 'balance-sheet'),
    financialFormulaOperator('+'),
    financialFormulaOperand('unclassifiedDebt', 'Other / Unclassified Debt', 'balance-sheet'),
);
const netDebtFormula = createFinancialFormula(
    financialFormulaOperand('totalDebt', 'Total Debt', 'balance-sheet'),
    financialFormulaOperator('−'),
    financialFormulaOperand('cashAndEquivalents', 'Cash + Equivalents', 'balance-sheet'),
);
const debtToEquityFormula = createFinancialFormula(
    financialFormulaOperand('totalDebt', 'Total Debt', 'balance-sheet'),
    financialFormulaOperator('÷'),
    financialFormulaOperand('shareholderEquity', 'Shareholders’ Equity', 'balance-sheet'),
);

export const FinancialOverviewCorporateDebt: FC<FinancialOverviewCorporateDebtProps> = ({
    preferenceKey = 'default',
    section,
    tickerSymbol,
}) => {
    const { token } = useAntDesignTheme();
    const [view, setView] = useFinancialOverviewViewPreference(
        preferenceKey,
        'corporateDebt',
        'trend',
        isCorporateDebtView,
    );
    const unclassifiedDebt = section?.unclassifiedDebt ?? 0;
    const hasDebtData = Boolean(
        section &&
        (section.totalDebt !== 0 || section.notes.length > 0 || section.history.some((point) => point.totalDebt !== 0)),
    );
    const longTermShare = section?.totalDebt
        ? Math.max(0, Math.min(100, (section.longTermDebt / section.totalDebt) * 100))
        : 0;
    const shortTermShare = section?.totalDebt
        ? Math.max(0, Math.min(100, (section.shortTermDebt / section.totalDebt) * 100))
        : 0;
    const unclassifiedShare = section?.totalDebt
        ? Math.max(0, Math.min(100, (unclassifiedDebt / section.totalDebt) * 100))
        : 0;
    const asOf = section?.asOf && dayjs(section.asOf).isValid() ? dayjs(section.asOf).format('MMM D, YYYY') : null;

    return (
        <FinancialOverviewPanel
            actions={
                <div style={{ maxWidth: '100%', width: '12rem' }}>
                    <DropdownButtonSelect<CorporateDebtView>
                        aria-label='Select corporate debt view'
                        header={
                            <DropdownMenuLabel icon={<TbCashBanknote size='0.875rem' />}>Debt View</DropdownMenuLabel>
                        }
                        matchTriggerWidth={false}
                        menuMinWidth='16rem'
                        onChange={(value) => value && setView(value)}
                        options={[
                            {
                                description: 'Review reported notes, coupon rates, and maturity context.',
                                icon: <TbFileText />,
                                label: 'Outstanding Instruments',
                                value: 'instruments',
                            },
                            {
                                description: 'Compare total and net debt across reported annual or quarterly periods.',
                                icon: <TbChartLine />,
                                label: 'Debt Trend',
                                value: 'trend',
                            },
                        ]}
                        placement='bottomRight'
                        size='small'
                        value={view}
                    />
                </div>
            }
            description={`Review debt composition, reported financing instruments, and liquidity coverage${asOf ? ` as of ${asOf}` : ''}.`}
            icon={<TbCashBanknote style={{ verticalAlign: '-0.125rem' }} />}
            title='Corporate Debt'
        >
            {hasDebtData && section ? (
                <div style={{ display: 'grid', gap: '1rem' }}>
                    <FinancialOverviewMetricCardGrid
                        showDescriptionTooltips
                        items={[
                            {
                                description: 'Current and long-term interest-bearing obligations.',
                                key: 'total-debt',
                                label: 'Total Debt',
                                tooltip: (
                                    <FinancialMetricFormulaTooltip
                                        description='Current, long-term, and other reported interest-bearing obligations.'
                                        formula={totalDebtFormula}
                                        label='Total Debt'
                                    />
                                ),
                                value: <FinancialValue value={section.totalDebt} />,
                            },
                            {
                                description: 'Debt remaining after available cash and cash-equivalent liquidity.',
                                key: 'net-debt',
                                label: 'Net Debt',
                                tooltip: (
                                    <FinancialMetricFormulaTooltip
                                        description='Debt remaining after available cash and cash-equivalent liquidity.'
                                        formula={netDebtFormula}
                                        label='Net Debt'
                                    />
                                ),
                                value: <FinancialValue value={section.netDebt} />,
                            },
                            {
                                description: 'Interest-bearing obligations expected beyond the next twelve months.',
                                key: 'long-term-debt',
                                label: 'Long-Term Debt',
                                value: <FinancialValue value={section.longTermDebt} />,
                            },
                            {
                                description: 'Interest-bearing obligations due within the next twelve months.',
                                key: 'short-term-debt',
                                label: 'Short-Term Debt',
                                value: <FinancialValue value={section.shortTermDebt} />,
                            },
                            ...(unclassifiedDebt > 0
                                ? [
                                      {
                                          description:
                                              'Reported debt, including lease obligations, that the source statement does not allocate by term.',
                                          key: 'unclassified-debt',
                                          label: 'Other / Unclassified',
                                          value: <FinancialValue value={unclassifiedDebt} />,
                                      },
                                  ]
                                : []),
                            {
                                description: 'The company’s reliance on debt relative to shareholder equity.',
                                key: 'debt-to-equity',
                                label: 'Debt to Equity',
                                tooltip: (
                                    <FinancialMetricFormulaTooltip
                                        description='The company’s reliance on debt relative to shareholder equity.'
                                        formula={debtToEquityFormula}
                                        label='Debt to Equity'
                                    />
                                ),
                                value:
                                    section.debtToEquity === null
                                        ? '-'
                                        : `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(section.debtToEquity)}x`,
                            },
                        ]}
                    />
                    <div>
                        <div
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '0.375rem',
                            }}
                        >
                            <small style={{ color: token.colorTextSecondary }}>Debt Composition</small>
                            <small style={{ color: token.colorTextTertiary }}>
                                {longTermShare.toFixed(1)}% long term · {shortTermShare.toFixed(1)}% short term
                                {unclassifiedShare > 0 ? ` · ${unclassifiedShare.toFixed(1)}% other` : ''}
                            </small>
                        </div>
                        <PointerTrackingTooltip
                            content={
                                <ChartTooltipContent
                                    label='Debt Composition'
                                    rows={[
                                        {
                                            color: token.blue4,
                                            fill: token.blue2,
                                            key: 'long-term-debt',
                                            label: 'Long-Term Debt',
                                            value: (
                                                <>
                                                    <FinancialValue value={section.longTermDebt} /> (
                                                    {longTermShare.toFixed(1)}%)
                                                </>
                                            ),
                                        },
                                        {
                                            color: token.gold5,
                                            fill: token.gold2,
                                            key: 'short-term-debt',
                                            label: 'Short-Term Debt',
                                            value: (
                                                <>
                                                    <FinancialValue value={section.shortTermDebt} /> (
                                                    {shortTermShare.toFixed(1)}%)
                                                </>
                                            ),
                                        },
                                        {
                                            color: token.purple5,
                                            fill: token.purple2,
                                            key: 'unclassified-debt',
                                            label: 'Other / Unclassified Debt',
                                            value: (
                                                <>
                                                    <FinancialValue value={unclassifiedDebt} /> (
                                                    {unclassifiedShare.toFixed(1)}%)
                                                </>
                                            ),
                                        },
                                    ]}
                                />
                            }
                        >
                            <div
                                aria-label={`${longTermShare.toFixed(1)}% long-term debt, ${shortTermShare.toFixed(1)}% short-term debt, and ${unclassifiedShare.toFixed(1)}% other or unclassified debt`}
                                role='img'
                                style={{
                                    borderRadius: token.borderRadius,
                                    display: 'flex',
                                    height: '0.75rem',
                                    overflow: 'hidden',
                                    width: '100%',
                                }}
                            >
                                <span
                                    aria-hidden='true'
                                    style={{
                                        background: token.blue2,
                                        border: `1px solid ${token.blue4}`,
                                        borderRadius:
                                            shortTermShare > 0 || unclassifiedShare > 0
                                                ? `${token.borderRadius}px 0 0 ${token.borderRadius}px`
                                                : token.borderRadius,
                                        boxSizing: 'border-box',
                                        flex: `0 0 ${longTermShare}%`,
                                        minWidth: longTermShare > 0 ? 1 : 0,
                                    }}
                                />
                                <span
                                    aria-hidden='true'
                                    style={{
                                        background: token.gold2,
                                        border: `1px solid ${token.gold5}`,
                                        borderRadius:
                                            longTermShare > 0
                                                ? unclassifiedShare > 0
                                                    ? 0
                                                    : `0 ${token.borderRadius}px ${token.borderRadius}px 0`
                                                : unclassifiedShare > 0
                                                  ? `${token.borderRadius}px 0 0 ${token.borderRadius}px`
                                                  : token.borderRadius,
                                        boxSizing: 'border-box',
                                        flex: `0 0 ${shortTermShare}%`,
                                        minWidth: shortTermShare > 0 ? 1 : 0,
                                    }}
                                />
                                <span
                                    aria-hidden='true'
                                    style={{
                                        background: token.purple2,
                                        border: `1px solid ${token.purple5}`,
                                        borderRadius:
                                            longTermShare > 0 || shortTermShare > 0
                                                ? `0 ${token.borderRadius}px ${token.borderRadius}px 0`
                                                : token.borderRadius,
                                        boxSizing: 'border-box',
                                        flex: `0 0 ${unclassifiedShare}%`,
                                        minWidth: unclassifiedShare > 0 ? 1 : 0,
                                    }}
                                />
                            </div>
                        </PointerTrackingTooltip>
                    </div>
                    <Divider style={{ margin: 0 }} />
                    {view === 'instruments' ? (
                        section.notes.length ? (
                            <DebtInstrumentsList notes={section.notes} tickerSymbol={tickerSymbol} />
                        ) : (
                            <EmptySection
                                description='No individual company-note instruments were reported for this symbol.'
                                height='10rem'
                                icon={<TbFileText size='1.25rem' />}
                                label='No Reported Notes'
                            />
                        )
                    ) : section.history.length ? (
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <DebtTrendView
                                preferenceKey={preferenceKey}
                                section={section}
                                tickerSymbol={tickerSymbol}
                            />
                            {section.notes.length ? (
                                <DebtInstrumentsList notes={section.notes} tickerSymbol={tickerSymbol} />
                            ) : null}
                        </div>
                    ) : (
                        <EmptySection
                            description='Historical debt balances are not currently available for this company.'
                            height='10rem'
                            icon={<TbChartLine size='1.25rem' />}
                            label='Debt History Unavailable'
                        />
                    )}
                </div>
            ) : (
                <EmptySection
                    description='Debt balances and financing instruments are not currently available for this company.'
                    height='16rem'
                    icon={<TbCashBanknote size='1.25rem' />}
                    label='Corporate Debt Unavailable'
                />
            )}
        </FinancialOverviewPanel>
    );
};
